import { useState, useEffect, useRef, useCallback } from "react";
import {
  AlertTriangle,
  Brain,
  Info,
  Loader2,
  Sparkles,
  Zap,
} from "lucide-react";
import { sendTrackDataServer } from "@/features/analytics/utils/serverTrackData";
import { useAuth } from "@/features/authentication";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { MoneyInput } from "@/components/inputs/money-input/money-input";
import {
  SearchAutocomplete,
  type SearchAutocompleteHandle,
} from "@/components/inputs/search-autocomplete/search-autocomplete";
import { useSearchSpaces } from "@/hooks/useSearchSpaces";
import { spaceService } from "@/services/space-service";
import { normalizeString } from "@/utils/normalize-string";
import { useImportDeal } from "../hooks/useImportDeal";
import type { DiagnosisData, ImportMissingField, LivePreview } from "../types";
import { Divider } from "../components/Divider";
import { HubSpotImportCard } from "../components/HubSpotImportCard";
import {
  planBriefHydration,
  type BriefSuggestion,
} from "../utils/briefHydration";

const SEGMENT_OPTIONS = [
  "Tecnologia",
  "Bens de Consumo",
  "Financeiro",
  "Saúde",
  "Educação",
  "Varejo",
  "Serviços",
  "Indústria",
  "Outro",
];

interface DiagnosisStepProps {
  data: DiagnosisData | null;
  onNext: (data: DiagnosisData) => void;
  onPreviewUpdate?: (data: Partial<LivePreview>) => void;
  onValidityChange?: (valid: boolean) => void;
  registerNext?: (fn: () => void) => void;
  isEditMode?: boolean;
  /** Activates quick-search mode (PED-12650); hidden when absent. */
  onQuickSearch?: () => void;
}

export default function DiagnosisStep({
  data,
  onNext,
  onPreviewUpdate,
  onValidityChange,
  registerNext,
  isEditMode = false,
  onQuickSearch,
}: DiagnosisStepProps) {
  const { userInfo } = useAuth();
  const [dealId, setDealId] = useState(data?.dealId ?? "");
  const [quickSearchConfirmed, setQuickSearchConfirmed] = useState(false);
  const {
    status: importStatus,
    errorKind: importErrorKind,
    extractionStatus,
    fullBrief,
    importDeal,
    reset: resetImport,
  } = useImportDeal();
  const [importMissingFields, setImportMissingFields] = useState<
    ImportMissingField[]
  >([]);
  // Divergências IA × formulário — nunca aplicadas automaticamente.
  const [aiSuggestions, setAiSuggestions] = useState<BriefSuggestion[]>([]);
  // Last values a HubSpot import wrote into the form, used to tell "leftover
  // from a previous import" (cleared on re-import) apart from manual input
  // (preserved), and to force-refresh explicit re-imports of the same deal.
  const lastImportRef = useRef<{
    dealId?: string;
    companyName?: string;
    contactName?: string;
    contactEmail?: string;
    targetCity?: string;
    positionsMin?: number;
    positionsMax?: number;
  }>({});
  const locationRef = useRef<SearchAutocompleteHandle>(null);

  const [companyName, setCompanyName] = useState(data?.companyName ?? "");
  const [segment, setSegment] = useState(data?.segment ?? "");
  const [contactName, setContactName] = useState(data?.contactName ?? "");
  const [contactEmail, setContactEmail] = useState(data?.contactEmail ?? "");
  const [targetCity, setTargetCity] = useState(data?.targetCity ?? "");
  const [selectedLocation, setSelectedLocation] = useState<
    DiagnosisData["selectedLocation"]
  >(data?.selectedLocation ?? null);
  const [importedData, setImportedData] = useState<
    DiagnosisData["importedData"]
  >(data?.importedData ?? null);
  const [positionsMin, setPositionsMin] = useState<number | "">(
    data?.positionsMin ?? "",
  );
  const [positionsMax, setPositionsMax] = useState<number | "">(
    data?.positionsMax ?? "",
  );
  const [monthlyBudget, setMonthlyBudget] = useState(data?.monthlyBudget ?? "");
  const budgetRef = useRef<HTMLInputElement>(null);
  const [needs, setNeeds] = useState(data?.needs ?? "");
  const [unavailableDaysEnabled, setUnavailableDaysEnabled] = useState(
    () => (data?.includeUnavailableWithinPeriod ?? 30) > 0,
  );
  const [unavailableDaysValue, setUnavailableDaysValue] = useState<string>(
    () =>
      data?.includeUnavailableWithinPeriod != null
        ? String(data.includeUnavailableWithinPeriod)
        : "30",
  );

  function normalizeMonthlyBudgetForSave(raw: string): string {
    if (raw == null || String(raw).trim() === "") return "";
    const s = String(raw).replace(/\./g, "").replace(",", ".");
    const num = parseFloat(s);
    return Number.isNaN(num) ? "" : num.toFixed(2);
  }

  const {
    spaces,
    handleSearchSpaces,
    isLoading: isSearching,
  } = useSearchSpaces();

  // Geocodifica a cidade vinda do import/IA e SELECIONA a localização
  // automaticamente (mesmo padrão do restore de proposta salva) — sem isso o
  // import só preenche o texto e o usuário precisa escolher no dropdown.
  // Retorna se selecionou; quem chama só abre o dropdown (focus) em falha.
  const autoSelectCityLocation = useCallback(
    async (label: string): Promise<boolean> => {
      try {
        const response = await spaceService.getSpaceByLocation(label);
        const result = Array.isArray(response) ? response[0] : response;
        if (result?.lat != null && result?.lng != null) {
          setSelectedLocation({
            label,
            lat: Number(result.lat),
            lng: Number(result.lng),
          });
          return true;
        }
      } catch {
        // geocode falhou — usuário seleciona manualmente (hint já orienta)
      }
      return false;
    },
    [],
  );

  const handleLocationSelect = useCallback(
    async (
      value: string,
      spaceData?: { address: string; name: string; placeId: string },
    ) => {
      if (!spaceData) {
        setTargetCity("");
        setSelectedLocation(null);
        onPreviewUpdate?.({ targetCity: "" });
        return;
      }
      const label =
        `${spaceData.name ?? ""}${spaceData.address ? ` - ${spaceData.address}` : ""}`.trim() ||
        value;
      setTargetCity(label);
      onPreviewUpdate?.({ targetCity: label });

      try {
        const response = await spaceService.getSpaceByLocation(label);
        const result = Array.isArray(response) ? response[0] : response;
        if (result?.lat != null && result?.lng != null) {
          setSelectedLocation({
            label,
            lat: Number(result.lat),
            lng: Number(result.lng),
          });
        } else {
          setSelectedLocation(null);
        }
      } catch {
        setSelectedLocation(null);
      }
    },
    [onPreviewUpdate],
  );

  const hasValidLocationSelection =
    !!selectedLocation &&
    selectedLocation.lat != null &&
    selectedLocation.lng != null &&
    !(selectedLocation.lat === 0 && selectedLocation.lng === 0);

  // When restored from a saved proposal, auto-geocode if the location text is
  // present but coordinates are missing or zero (e.g. imported from deal).
  useEffect(() => {
    const storedCity = data?.targetCity;
    const storedLocation = data?.selectedLocation;
    const needsGeocode =
      !!storedCity &&
      (!storedLocation ||
        storedLocation.lat === 0 ||
        storedLocation.lng === 0 ||
        storedLocation.lat == null ||
        storedLocation.lng == null);
    if (!needsGeocode) return;
    spaceService
      .getSpaceByLocation(storedCity)
      .then((response) => {
        const result = Array.isArray(response) ? response[0] : response;
        if (result?.lat != null && result?.lng != null) {
          setSelectedLocation({
            label: storedCity,
            lat: Number(result.lat),
            lng: Number(result.lng),
          });
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // A extração IA em andamento NÃO bloqueia o avanço: a busca do passo
  // seguinte é determinística (usa os campos do formulário) e o brief
  // completo é conciliado pelo wizard quando chegar. O gate real fica no
  // "Gerar Proposta" (AIModeWizard), único ponto que consome o brief.
  const isValid =
    !!dealId.trim() &&
    !!companyName.trim() &&
    !!contactName.trim() &&
    !!contactEmail.trim() &&
    hasValidLocationSelection &&
    positionsMin !== "" &&
    positionsMax !== "";

  const handleNextRef = useRef<() => void>(() => {});
  handleNextRef.current = () => {
    if (!isValid) return;
    const payload = {
      dealId,
      companyName,
      contactName,
      contactEmail,
      segment,
      targetCity,
      selectedLocation,
      importedData,
      // Not a form field — carried invisibly from the import so the persisted
      // proposal can route the WhatsApp consultant / SMB gate downstream.
      // A rehydrated proposal has these persisted but no importedData, so the
      // stored values must survive advancing the step without a fresh import.
      numberOfEmployees:
        importedData?.company_size ?? data?.numberOfEmployees ?? null,
      dealInsights: importedData?.deal_insights ?? data?.dealInsights ?? [],
      positionsMin,
      positionsMax,
      monthlyBudget: normalizeMonthlyBudgetForSave(
        budgetRef.current?.value ?? monthlyBudget,
      ),
      needs,
      includeUnavailableWithinPeriod:
        unavailableDaysEnabled && unavailableDaysValue
          ? Number(unavailableDaysValue) || undefined
          : undefined,
    };
    onNext(payload);
  };

  useEffect(() => {
    registerNext?.(() => handleNextRef.current());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    onValidityChange?.(isValid);
  }, [isValid, onValidityChange]);

  useEffect(() => {
    onPreviewUpdate?.({ positionsMin, positionsMax });
  }, [positionsMin, positionsMax, onPreviewUpdate]);

  useEffect(() => {
    if (data?.dealInsights && data.dealInsights.length > 0) {
      onPreviewUpdate?.({ dealInsights: data.dealInsights });
    }
  }, [data?.dealInsights, onPreviewUpdate]);

  // Hidrata o formulário quando o brief completo chega da extração em
  // background: preenche só campos vazios; divergências viram sugestões
  // dismissíveis (nunca sobrescreve CRM ou digitação do usuário).
  const hydratedBriefRef = useRef<typeof fullBrief>(null);
  useEffect(() => {
    if (extractionStatus !== "ready" || !fullBrief) return;
    if (hydratedBriefRef.current === fullBrief) return;
    hydratedBriefRef.current = fullBrief;

    const plan = planBriefHydration(
      fullBrief,
      {
        needs,
        companyName,
        contactName,
        contactEmail,
        segment,
        positionsMin,
        positionsMax,
        targetCity,
      },
      SEGMENT_OPTIONS,
    );

    if (plan.fills.needs !== undefined) setNeeds(plan.fills.needs);
    if (plan.fills.companyName !== undefined) {
      setCompanyName(plan.fills.companyName);
      lastImportRef.current.companyName = plan.fills.companyName;
      onPreviewUpdate?.({ companyName: plan.fills.companyName });
    }
    if (plan.fills.contactEmail !== undefined) {
      setContactEmail(plan.fills.contactEmail);
      lastImportRef.current.contactEmail = plan.fills.contactEmail;
    }
    if (plan.fills.contactName !== undefined) {
      setContactName(plan.fills.contactName);
      lastImportRef.current.contactName = plan.fills.contactName;
    }
    if (plan.fills.segment !== undefined) setSegment(plan.fills.segment);
    if (plan.fills.positionsMin !== undefined) {
      setPositionsMin(plan.fills.positionsMin);
      lastImportRef.current.positionsMin = plan.fills.positionsMin;
    }
    if (plan.fills.positionsMax !== undefined) {
      setPositionsMax(plan.fills.positionsMax);
      lastImportRef.current.positionsMax = plan.fills.positionsMax;
    }
    if (plan.fills.city !== undefined) {
      setTargetCity(plan.fills.city);
      lastImportRef.current.targetCity = plan.fills.city;
      onPreviewUpdate?.({ targetCity: plan.fills.city });
      handleSearchSpaces(plan.fills.city);
      void autoSelectCityLocation(plan.fills.city);
    }
    setAiSuggestions(plan.suggestions);

    // Brief completo substitui o parcial: alimenta os insights do preview e
    // o payload da proposta (soft preferences, cultural fit, narrativa).
    setImportedData(fullBrief);
    onPreviewUpdate?.({ dealInsights: fullBrief.deal_insights ?? [] });
  }, [extractionStatus, fullBrief]); // eslint-disable-line react-hooks/exhaustive-deps

  function applySuggestion(suggestion: BriefSuggestion) {
    if (suggestion.apply.positionsMin !== undefined) {
      setPositionsMin(suggestion.apply.positionsMin);
      lastImportRef.current.positionsMin = suggestion.apply.positionsMin;
    }
    if (suggestion.apply.positionsMax !== undefined) {
      setPositionsMax(suggestion.apply.positionsMax);
      lastImportRef.current.positionsMax = suggestion.apply.positionsMax;
    }
    if (suggestion.apply.city) {
      const city = suggestion.apply.city;
      setTargetCity(city);
      setSelectedLocation(null);
      lastImportRef.current.targetCity = city;
      onPreviewUpdate?.({ targetCity: city });
      handleSearchSpaces(city);
      void autoSelectCityLocation(city).then((selected) => {
        if (!selected) setTimeout(() => locationRef.current?.focus(), 50);
      });
    }
    setAiSuggestions((prev) => prev.filter((s) => s !== suggestion));
  }

  function dismissSuggestion(suggestion: BriefSuggestion) {
    setAiSuggestions((prev) => prev.filter((s) => s !== suggestion));
  }

  /**
   * When the import lacks a value, keep the user's manual input but clear
   * leftovers from a PREVIOUS import — otherwise re-importing a different
   * deal would silently attach the old deal's data to the new one.
   */
  function resolveImportedField(
    imported: string | null | undefined,
    current: string,
    lastImported: string | undefined,
  ): string {
    if (imported) return imported;
    return current && current !== lastImported ? current : "";
  }

  async function handleImport() {
    if (!dealId.trim()) return;
    setImportMissingFields([]);
    setAiSuggestions([]);
    hydratedBriefRef.current = null;
    // Explicit re-import of the same deal (or an edit-mode refresh) must see
    // fresh HubSpot data — e.g. after the user attaches the missing company.
    const forceRefresh =
      isEditMode || lastImportRef.current.dealId === dealId.trim();
    const result = await importDeal(dealId.trim(), { forceRefresh });
    if (!result) return;

    // Partial import: prefill whatever HubSpot has and list what's missing
    // for the user to fill in manually.
    const prev = lastImportRef.current;
    const missingFields: ImportMissingField[] = [];

    const nextCompanyName = resolveImportedField(
      result.company_name,
      companyName,
      prev.companyName,
    );
    if (!result.company_name) missingFields.push("company");
    setCompanyName(nextCompanyName);

    const city = result.hard_constraints.city;
    if (city) {
      setTargetCity(city);
      setSelectedLocation(null);
      onPreviewUpdate?.({ targetCity: city });
      handleSearchSpaces(city);
      // Focar o campo abre o dropdown (onFocus do autocomplete) — só faz
      // sentido quando a seleção automática falhou e o usuário precisa agir.
      void autoSelectCityLocation(city).then((selected) => {
        if (!selected) setTimeout(() => locationRef.current?.focus(), 50);
      });
    } else {
      missingFields.push("city");
      // Clear a city left over from a previous import (manual picks survive).
      if (targetCity && targetCity === prev.targetCity) {
        setTargetCity("");
        setSelectedLocation(null);
        onPreviewUpdate?.({ targetCity: "" });
      }
    }

    const minCap = result.hard_constraints.min_capacity;
    const maxCap = result.hard_constraints.max_capacity;
    // The backend cannot represent "unknown capacity": HardConstraints
    // defaults both fields to 1, so 1/1 (or null/null) is treated as missing
    // rather than silently overwriting typed positions with the sentinel.
    const capacityMissing =
      (minCap == null && maxCap == null) || (minCap === 1 && maxCap === 1);
    if (capacityMissing) {
      missingFields.push("capacity");
      if (positionsMin !== "" && positionsMin === prev.positionsMin) {
        setPositionsMin("");
      }
      if (positionsMax !== "" && positionsMax === prev.positionsMax) {
        setPositionsMax("");
      }
    } else {
      setPositionsMin(minCap ?? "");
      setPositionsMax(maxCap ?? "");
    }

    const nextContactEmail = resolveImportedField(
      result.primary_contact_email,
      contactEmail,
      prev.contactEmail,
    );
    if (!result.primary_contact_email) missingFields.push("contact_email");
    setContactEmail(nextContactEmail);

    const nextContactName = resolveImportedField(
      result.primary_contact_name,
      contactName,
      prev.contactName,
    );
    if (!result.primary_contact_name) missingFields.push("contact_name");
    setContactName(nextContactName);

    if (result.summary_narrative) {
      setNeeds(result.summary_narrative);
    }

    // Segment is only prefilled when the HubSpot industry matches one of the
    // fixed select options (accent-insensitive); an unmatched industry is
    // surfaced as missing instead of being dropped silently.
    const industry = result.industry?.trim();
    const matchedSegment = industry
      ? SEGMENT_OPTIONS.find(
          (option) => normalizeString(option) === normalizeString(industry),
        )
      : undefined;
    if (industry && !matchedSegment) missingFields.push("segment");
    setSegment(matchedSegment ?? "");
    setMonthlyBudget("");

    setImportMissingFields(missingFields);
    setImportedData(result);
    lastImportRef.current = {
      dealId: dealId.trim(),
      companyName: result.company_name ?? undefined,
      contactName: result.primary_contact_name ?? undefined,
      contactEmail: result.primary_contact_email ?? undefined,
      targetCity: city || undefined,
      positionsMin: capacityMissing ? undefined : (minCap ?? undefined),
      positionsMax: capacityMissing ? undefined : (maxCap ?? undefined),
    };

    sendTrackDataServer({
      eventName: "VISION_AI_MODE_DEAL_IMPORTED",
      productType: "PO",
      url: window.location.pathname,
      data: {
        deal_id: dealId.trim(),
        user_id: userInfo?.user?.id ?? null,
        email: userInfo?.profile?.email ?? null,
        company_name: result.company_name,
        city: result.hard_constraints.city || null,
        positions_min: result.hard_constraints.min_capacity ?? null,
        positions_max: result.hard_constraints.max_capacity ?? null,
        has_summary_narrative: !!result.summary_narrative,
        missing_fields: missingFields,
        extraction_status: result.extraction_status ?? "ready",
      },
    });

    onPreviewUpdate?.({
      dealImported: true,
      companyName: nextCompanyName,
      dealInsights: result.deal_insights ?? [],
      ...(city ? { targetCity: city } : {}),
    });
  }

  return (
    <div className="space-y-5">
      <div>
        <span
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold mb-4"
          style={{
            background:
              "linear-gradient(135deg, rgba(124,58,237,0.12), rgba(255,217,0,0.08))",
            border: "1px solid rgba(167,139,250,0.2)",
            color: "#7C3AED",
          }}
        >
          <Brain className="w-3.5 h-3.5" />
          Diagnostico AI
        </span>
        <h2
          className="text-2xl font-bold mt-4 mb-1"
          style={{ color: "#1A1814" }}
        >
          Importar do CRM
        </h2>
        <p className="text-sm" style={{ color: "#78736D" }}>
          Conecte com HubSpot para preencher automaticamente
        </p>
      </div>

      <HubSpotImportCard
        dealId={dealId}
        importStatus={importStatus}
        errorKind={importErrorKind}
        missingFields={importMissingFields}
        onDealIdChange={(value) => {
          setDealId(value);
          if (importStatus !== "idle") {
            resetImport();
            setImportMissingFields([]);
            setAiSuggestions([]);
          }
        }}
        onImport={handleImport}
        isEditMode={isEditMode}
      />

      {onQuickSearch && !isEditMode && (
        <div
          className="rounded-2xl p-4 space-y-3"
          style={{
            background: "#F8F7F6",
            border: "1px solid rgba(232,230,227,0.5)",
          }}
        >
          <div className="flex items-start gap-2.5">
            <Zap
              className="w-4 h-4 mt-0.5 shrink-0"
              style={{ color: "#78736D" }}
            />
            <div>
              <p className="text-sm font-semibold" style={{ color: "#43403D" }}>
                Busca rápida
              </p>
              <p className="text-xs" style={{ color: "#78736D" }}>
                Consulte a disponibilidade de salas sem preencher os dados do
                deal.
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Checkbox
              id="quick-search-confirm"
              checked={quickSearchConfirmed}
              onCheckedChange={(checked) =>
                setQuickSearchConfirmed(checked === true)
              }
            />
            <Label
              htmlFor="quick-search-confirm"
              className="text-xs font-medium leading-snug cursor-pointer"
              style={{ color: "#43403D" }}
            >
              Estou ciente de que os dados desta busca não serão capturados
            </Label>
          </div>
          <button
            type="button"
            onClick={onQuickSearch}
            disabled={!quickSearchConfirmed}
            className="flex items-center gap-2 px-5 py-2.5 rounded-[14px] text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: "#43403D", color: "white" }}
          >
            <Zap className="w-4 h-4" />
            Iniciar busca rápida
          </button>
          {/* Aviso fixo (não modal) abaixo do botão — regra do refinamento PED-12650 */}
          <div
            role="note"
            className="flex items-start gap-2 text-xs rounded-xl px-3 py-2.5"
            style={{
              background: "#FFFBEB",
              border: "1px solid rgba(217,119,6,0.2)",
              color: "#B45309",
            }}
          >
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>
              Os dados da busca rápida não serão capturados nem associados a um
              deal, e não alimentam o algoritmo de match. O uso é registrado com
              sua identificação para auditoria.
            </span>
          </div>
        </div>
      )}

      {extractionStatus === "pending" && (
        <div
          role="status"
          className="flex items-center gap-2 text-xs rounded-xl px-3 py-2.5"
          style={{
            background: "#F5F3FF",
            border: "1px solid rgba(167,139,250,0.25)",
            color: "#6D28D9",
            animation: "aiStepEnter 0.3s ease",
          }}
        >
          <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
          <span>
            IA analisando os engagements do deal para completar o diagnóstico…
            Você já pode revisar os campos abaixo.
          </span>
        </div>
      )}

      {extractionStatus === "failed" && (
        <div
          role="alert"
          className="flex items-start gap-2 text-xs rounded-xl px-3 py-2.5"
          style={{
            background: "#FFFBEB",
            border: "1px solid rgba(217,119,6,0.2)",
            color: "#B45309",
            animation: "aiStepEnter 0.3s ease",
          }}
        >
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>
            A análise IA não foi concluída. Você pode continuar preenchendo
            manualmente ou reimportar o deal para tentar novamente.
          </span>
        </div>
      )}

      {aiSuggestions.map((suggestion) => (
        <div
          key={suggestion.field}
          className="flex items-start gap-2 text-xs rounded-xl px-3 py-2.5"
          style={{
            background: "#F5F3FF",
            border: "1px solid rgba(167,139,250,0.25)",
            color: "#5B21B6",
            animation: "aiStepEnter 0.3s ease",
          }}
        >
          <Sparkles className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span className="flex-1">{suggestion.message}</span>
          <button
            type="button"
            onClick={() => applySuggestion(suggestion)}
            className="font-semibold underline underline-offset-2 shrink-0"
            style={{ color: "#6D28D9" }}
          >
            Aplicar
          </button>
          <button
            type="button"
            onClick={() => dismissSuggestion(suggestion)}
            className="font-medium shrink-0"
            style={{ color: "#9E9A94" }}
          >
            Dispensar
          </button>
        </div>
      ))}

      <Divider label="Dados do cliente" />

      <div>
        <Label
          className="block text-xs font-medium mb-2"
          style={{ color: "#43403D" }}
        >
          Nome da empresa *
        </Label>
        <Input
          type="text"
          value={companyName}
          onChange={(e) => {
            setCompanyName(e.target.value);
            onPreviewUpdate?.({ companyName: e.target.value });
          }}
          placeholder="Ex: AB InBev"
        />
      </div>

      <div>
        <Label
          className="block text-xs font-medium mb-2"
          style={{ color: "#43403D" }}
        >
          Segmento
        </Label>
        <select
          aria-label="Segmento"
          value={segment}
          onChange={(e) => setSegment(e.target.value)}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <option value="">Selecione...</option>
          {SEGMENT_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label
            className="block text-xs font-medium mb-2"
            style={{ color: "#43403D" }}
          >
            Nome do cliente *
          </Label>
          <Input
            type="text"
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            placeholder="Nome completo do cliente"
          />
        </div>
        <div>
          <Label
            className="block text-xs font-medium mb-2"
            style={{ color: "#43403D" }}
          >
            Email do cliente *
          </Label>
          <Input
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            placeholder="email@cliente.com"
          />
        </div>
      </div>

      <div>
        <Label
          className="block text-xs font-medium mb-2"
          style={{ color: "#43403D" }}
        >
          Localização *
        </Label>
        <SearchAutocomplete
          ref={locationRef}
          placeholder="Buscar cidade, bairro ou endereço..."
          className="!mt-0"
          listClassName="!absolute !left-0 !w-full !top-auto"
          options={spaces.map((s) => {
            const fullLabel =
              `${s.name ?? ""}${s.address ? ` - ${s.address}` : ""}`.trim() ||
              s.name ||
              s.address;
            return {
              value: s.placeId,
              label: fullLabel,
              description: s.address,
              data: s,
            };
          })}
          value={targetCity}
          onValueChange={(val, spaceData) =>
            handleLocationSelect(val, spaceData)
          }
          onInputChange={(val) => {
            setTargetCity(val);
            setSelectedLocation(null);
            onPreviewUpdate?.({ targetCity: val });
            handleSearchSpaces(val);
          }}
          isLoading={isSearching}
        />
        {targetCity.trim() && !hasValidLocationSelection && (
          <p className="text-xs mt-1" style={{ color: "#B91C1C" }} role="alert">
            Selecione uma localização da lista para continuar.
          </p>
        )}
      </div>

      <div>
        <Label
          className="block text-xs font-medium mb-2"
          style={{ color: "#43403D" }}
        >
          Capacidade *
        </Label>
        <div className="flex gap-2 items-center">
          <div className="flex-1">
            <Input
              type="number"
              placeholder="De"
              min={1}
              value={positionsMin}
              onChange={(e) =>
                setPositionsMin(
                  e.target.value === "" ? "" : Number(e.target.value),
                )
              }
            />
          </div>
          <span className="text-gray-400 text-sm">—</span>
          <div className="flex-1">
            <Input
              type="number"
              placeholder="Até"
              min={1}
              value={positionsMax}
              onChange={(e) =>
                setPositionsMax(
                  e.target.value === "" ? "" : Number(e.target.value),
                )
              }
            />
          </div>
        </div>
      </div>

      <div>
        <Label
          className="block text-xs font-medium mb-2"
          style={{ color: "#43403D" }}
        >
          Custo atual mensal (R$)
        </Label>
        <MoneyInput
          ref={budgetRef}
          label="Custo atual mensal"
          showlabel={false}
          placeholder="0,00"
          prefix="R$"
          defaultValue={monthlyBudget}
        />
      </div>

      <div>
        <Label
          className="block text-xs font-medium mb-2"
          style={{ color: "#43403D" }}
        >
          Necessidades específicas
        </Label>
        <textarea
          value={needs}
          onChange={(e) => setNeeds(e.target.value)}
          placeholder="Ex: Sala de reunião para 8 pessoas, área de descompressão..."
          rows={4}
          maxLength={500}
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-y"
        />
      </div>

      <Divider label="Disponibilidade" />

      <div>
        <div className="flex items-start justify-between gap-3">
          <div>
            <Label className="text-xs font-medium" style={{ color: "#43403D" }}>
              Incluir salas com disponibilidade futura
            </Label>
            <p className="text-xs mt-0.5" style={{ color: "#9E9A94" }}>
              Exibe salas ocupadas com previsão de retorno ao inventário dentro
              do período informado. A data pode mudar caso o contrato atual seja
              renovado.
            </p>
          </div>
          <Switch
            checked={unavailableDaysEnabled}
            onCheckedChange={setUnavailableDaysEnabled}
            aria-label="Incluir salas com disponibilidade futura"
          />
        </div>
        {unavailableDaysEnabled && (
          <div className="flex items-center gap-2 mt-3">
            <span className="text-xs" style={{ color: "#78736D" }}>
              Disponíveis nos próximos
            </span>
            <div className="w-16">
              <Input
                type="number"
                min={1}
                value={unavailableDaysValue}
                onChange={(e) => {
                  const raw = e.target.value.replace(/\D/g, "");
                  const n = parseInt(raw, 10);
                  setUnavailableDaysValue(
                    raw === "" ? "" : String(Math.max(1, n)),
                  );
                }}
                className="text-center"
                placeholder="30"
              />
            </div>
            <span className="text-xs" style={{ color: "#78736D" }}>
              dias
            </span>
          </div>
        )}
      </div>

      <div
        className="flex items-start gap-2.5 text-xs rounded-2xl p-4"
        style={{
          color: "#78736D",
          background: "#F8F7F6",
          border: "1px solid rgba(232,230,227,0.5)",
        }}
      >
        <Info
          className="w-3.5 h-3.5 mt-0.5 flex-shrink-0"
          style={{ color: "#9E9A94" }}
        />
        <span>
          Campos com * são obrigatórios. Importe do HubSpot para preencher
          automaticamente com dados do CRM.
        </span>
      </div>
    </div>
  );
}
